/**
 * useResilientEvent ✦ — iter194
 * ─────────────────────────────────────────────────────────────
 * Codifies the iter193 Booth OS resilience pattern into shared
 * infrastructure for every guest-facing surface (Booth OS, Memory
 * Wall, Lens Notes, Drop One Word, Memory Snap, etc.).
 *
 * Resilience philosophy (event-grade, not normal SaaS):
 *
 *    1. Preserve continuity
 *    2. Preserve emotional atmosphere
 *    3. Preserve interaction state
 *    4. Refresh silently
 *    5. Fail quietly
 *    6. Only hard-fail when impossible
 *
 * Behaviour:
 *
 *    - Hydrate IMMEDIATELY from sessionStorage (zero render gap on
 *      browser-back or tab switch).
 *    - Fetch in the background. On success, replace cache + state.
 *    - On fetch failure, keep serving the cached event silently.
 *      `isStale` flips to true so a surface CAN render a very
 *      subtle internal hint (we don't recommend; almost always
 *      silence is correct).
 *    - Only set `error` when there is NOTHING cached AND the
 *      fetcher rejected — the only state where a page genuinely
 *      cannot render.
 *    - Optional `refreshMs` schedules a periodic silent refresh
 *      (useful for long-running booth kiosks).
 *
 * The cache lives in sessionStorage — tab-scoped, dies on close.
 * No TTL by default: an event night is a single session and
 * showing stale-but-correct data is always better than blanking
 * the surface.
 *
 * Usage:
 *
 *    const { event, error, isStale, refresh } = useResilientEvent({
 *      cacheKey: `booth_os_event_${event_id}`,
 *      fetcher: () => axios.get(`${API}/events/${event_id}/booth-info`).then(r => r.data),
 *      enabled: !!event_id,
 *      // refreshMs: 60_000, // optional
 *      validate: (cached) => cached?.event_id === event_id,
 *    });
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const readCache = (key) => {
  if (!key) return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
};

const writeCache = (key, value) => {
  if (!key) return;
  try { sessionStorage.setItem(key, JSON.stringify(value)); } catch {}
};

export function useResilientEvent({
  cacheKey,
  fetcher,
  enabled = true,
  refreshMs = 0,
  validate = () => true,
  onSuccess,
} = {}) {
  // Hydrate synchronously from cache so the very first render has
  // data — no perceptible gap on browser-back / tab-resume.
  const [event, setEvent] = useState(() => {
    const cached = readCache(cacheKey);
    return cached && validate(cached) ? cached : null;
  });
  const [error, setError] = useState(null);
  const [isStale, setIsStale] = useState(false);

  // Latest props in refs so the refresh loop doesn't restart on
  // every render of the consumer.
  const fetcherRef = useRef(fetcher);
  const validateRef = useRef(validate);
  const successRef = useRef(onSuccess);
  fetcherRef.current = fetcher;
  validateRef.current = validate;
  successRef.current = onSuccess;

  const cancelledRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!enabled || !fetcherRef.current) return;
    try {
      const fresh = await fetcherRef.current();
      if (cancelledRef.current) return;
      if (fresh && validateRef.current(fresh)) {
        setEvent(fresh);
        writeCache(cacheKey, fresh);
        setError(null);
        setIsStale(false);
        if (typeof successRef.current === 'function') {
          successRef.current(fresh);
        }
      }
    } catch (err) {
      if (cancelledRef.current) return;
      // Resilience contract: only hard-fail when there is nothing
      // to render. If the cache has a valid event, stay silent —
      // the guest never sees infrastructure anxiety.
      const cached = readCache(cacheKey);
      if (cached && validateRef.current(cached)) {
        setIsStale(true);
      } else {
        setError(err);
      }
    }
  }, [cacheKey, enabled]);

  // Initial fetch on mount / cacheKey change.
  useEffect(() => {
    cancelledRef.current = false;
    if (!enabled) return undefined;
    // Re-hydrate from cache when cacheKey changes (e.g. swap event_id).
    const cached = readCache(cacheKey);
    if (cached && validateRef.current(cached)) {
      setEvent(cached);
      setError(null);
    } else {
      setEvent(null);
    }
    refresh();
    return () => { cancelledRef.current = true; };
  }, [cacheKey, enabled, refresh]);

  // Optional silent periodic refresh — long-running booth kiosks
  // benefit from this; one-shot guest pages do not.
  useEffect(() => {
    if (!enabled || !refreshMs || refreshMs <= 0) return undefined;
    const id = setInterval(refresh, refreshMs);
    return () => clearInterval(id);
  }, [enabled, refreshMs, refresh]);

  return { event, error, isStale, refresh };
}

export default useResilientEvent;
